/* eslint-disable react/jsx-indent */
import { forOwn, isEmpty, noop, uniqBy, isNumber, merge } from 'lodash';
import Logger from '@sm360/logger-sentry';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { ConfigsService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    Account,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    REDIRECTIONS,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { ValidationService, InputColorRadioContainer } from '@sm360/widget-commons-forms';
import { Vehicle, ORGANIZATIONS } from '@sm360/widget-commons-vehicle';
import { MessageBox } from '@sm360/widget-commons-basic-components';
import { InputNumber } from '@sm360/ui-library';

import CBBAutoComplete from 'trade-in-360/components/CBBAutoComplete/CBBAutoComplete.jsx';

import ShowroomCBBService from 'trade-in-360/services/ShowroomCBBService/ShowroomCBBService.js';
import VehicleOptions from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleOptions/VehicleOptions.jsx';
import WIDGET_VERSION from 'trade-in-360/constants/widgetVersions.js';
import FIELDS_VALIDATION from 'trade-in-360/constants/fieldsValidation.js';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import ErrorMessage from 'trade-in-360/components/ErrorMessage/ErrorMessage.jsx';

import 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleInfo/VehicleInfo.scss';
import NewTradeInContext from '../../../../NewTradeInContext';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';

const fieldValidations = {
    odometer: FIELDS_VALIDATION.validateOdometer,
    vin: () => true,
    year: ValidationService.validateYear,
};

class VehicleInfo extends Component {
    constructor(props, context) {
        super(props);

        const { tradeInVehicle } = props;

        if (tradeInVehicle && tradeInVehicle.estimation) {
            // set update estimation.amountWasUpdated = true otherwise it will create a new lead
            tradeInVehicle.estimation.amountWasUpdated = true;
        }

        this.state = {
            formValidation: {
                make: false,
                model: false,
                year: false,
                exteriorColor: false,
                interiorColor: false,
                odometer: false,
                vin: true,
            },
            hasBeenSubmitted: false,
            autoCompleteValue: '',
            tradeInVehicle,
            tradeInId: null,
            tradeInLeadId: null,
            account: props.account,
            displaySubmitError: false,
            displayCheckInfoMessage: false,
            estimation: null,
            profile: {},
            isLoggedIn: false,
            displayNotFoundError: false,
            displayLabel: false,
            displayAPISubmitError: undefined,
            fetching: false,
        };

        this.fillAutoCompleteValueFromTradeInVehicle = this.fillAutoCompleteValueFromTradeInVehicle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleTradeInChange = this.handleTradeInChange.bind(this);
        this.isFieldValid = this.isFieldValid.bind(this);
        this.onAutoCompleteSelect = this.onAutoCompleteSelect.bind(this);
        this.sendVehicleBasicInfo = this.sendVehicleBasicInfo.bind(this);
        this.validateVehicleInformationForm = this.validateVehicleInformationForm.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleColorRadioChange = this.handleColorRadioChange.bind(this);
        this.handleMileageChange = this.handleMileageChange.bind(this);
        this.handleVehicleOptionsChange = this.handleVehicleOptionsChange.bind(this);
        this.handleOdometerAnalytics = this.handleOdometerAnalytics.bind(this);

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());

        this.cbbAutoCompleteRef = null;
        this.sendingTradeIn = false;
        this.isTrade360 = context.version === WIDGET_VERSION.TRADE_360;
    }

    componentDidMount() {
        this.fillAutoCompleteValueFromTradeInVehicle();
        if (!this.props.isOfficialTradeIn) {
            AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_VEHICLE());
        } else {
            AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_VEHICLE_FORM());
        }
    }

    componentWillReceiveProps(newProps) {
        if (newProps.tradeInVehicle !== this.state.tradeInVehicle) {
            this.fillAutoCompleteValueFromTradeInVehicle(newProps.tradeInVehicle);
        }
    }

    onAutoCompleteSelect(autoCompleteValue, selectedValue) {
        let tradeInVehicle = null;
        const { odometer } = this.state.tradeInVehicle;
        const parsedOdometer = (isNumber(odometer) ? odometer : parseInt(odometer, 10)) || '';

        if (selectedValue) {
            // Reset exteriorColor validation
            this.setState({
                formValidation: {
                    ...this.state.formValidation,
                    exteriorColor: false,
                    interiorColor: false,
                    odometer: this.isFieldValid(parsedOdometer, 'odometer'),
                },
            });
            tradeInVehicle = {
                ...selectedValue,
                exteriorColor: undefined,
                interiorColor: undefined,
                odometer: parsedOdometer,
            };
        } else {
            tradeInVehicle = {
                exteriorColor: this.state.tradeInVehicle.exteriorColor,
                interiorColor: this.state.tradeInVehicle.interiorColor,
                odometer: parsedOdometer,
            };
        }

        if (autoCompleteValue) {
            this.setState({ displayLabel: true });
        }

        this.setState({ autoCompleteValue, tradeInVehicle, estimation: null }, () => {
            this.fillAutoCompleteValueFromTradeInVehicle(tradeInVehicle);
            this.props.onTradeInVehicleUpdated(tradeInVehicle);
        });
    }

    onCantFindVehicleClick() {
        const windowProxy = WindowProxy.get();
        const locale = LocaleService.getSimpleLocale();
        const organizationId = this?.props?.config?.orgId || ConfigsService.getOrganizationId();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();

        const isDilawri = organizationId === ORGANIZATIONS.DILAWRI;
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_CANT_FIND_VEHICLE_LINK());

        if (windowProxy) {
            if (isDilawri) {
                windowProxy.location.href = REDIRECTIONS[locale].tradeInAppraisalV25Dilawri;
            } else {
                windowProxy.location.href = isShowroomV25Enabled
                    ? REDIRECTIONS[locale].tradeInAppraisalV25
                    : REDIRECTIONS[locale].tradeInAppraisal;
            }
        }
    }

    getCBBAutoComplete() {
        return (
            <div
                className={`autocomplete-container ${
                    this.isTrade360 ? 'step-section step-section--autocomplete' : ''
                } ${this.state.displayLabel ? 'autocomplete-container--no-edit' : ''}`}
            >
                <div className="autocomplete-container-inner">
                    {this.isTrade360 ? (
                        <p className={WEB_CSS_CLASSNAMES.SUBHEADING1}>
                            {LocaleService.t('TRADE_IN.YOUR_VEHICLE_INFORMATION')}
                        </p>
                    ) : (
                        <p className="sr-text">{LocaleService.t('TRADE_IN.YOUR_VEHICLE')}</p>
                    )}
                    {!this.state.displayLabel ? (
                        // Display autocomplete text box with Can't find vehicle link
                        <div className="trade-in-form--autocomplete" key="vehicle-form">
                            <CBBAutoComplete
                                value={this.state.autoCompleteValue}
                                onValueSelected={this.onAutoCompleteSelect}
                                dataTestPrefix="trade-in.vehicle-form"
                                ref={(element) => {
                                    this.cbbAutoCompleteRef = element;
                                }}
                                displayOnWrapper
                                placeholder={this.isTrade360 ? LocaleService.t('TRADE_IN.AUTOCOMPLETE_LABEL') : ''}
                                config={this.props.config}
                                quote={this.props.quote}
                            />
                            <a
                                onClick={this.onCantFindVehicleClick}
                                className="trade-in-form--autocomplete-link link"
                                href="#"
                            >
                                {LocaleService.t('TRADE_IN.CANT_FIND_VEHICLE')}
                            </a>
                        </div>
                    ) : (
                        // Display label with Edit button
                        <div
                            className={`vehicle-info-form ${
                                this.isTrade360 ? '' : 'trade-in-form--autocomplete-label'
                            }`}
                        >
                            <label className={`${WEB_CSS_CLASSNAMES.BODY2} vehicle-info-value`}>
                                {this.state.tradeInVehicle && this.state.tradeInVehicle.value}
                            </label>
                        </div>
                    )}
                </div>
                {this.state.displayLabel &&
                    !this.context.fieldsLocked && (
                        <a
                            href="#"
                            onClick={this.handleEdit}
                            className="trade-in-form--autocomplete-link link modify-link"
                        >
                            {LocaleService.t('TRADE_IN.ESTIMATE.MODIFY')}
                        </a>
                    )}
            </div>
        );
    }

    getVehicleBasicInfoForm() {
        const exteriorColors = this.state.tradeInVehicle?.exteriorColors || [];
        const interiorColors = this.state.tradeInVehicle?.interiorColors || [];
        const otherColor = {
            description: 'Other',
            htmlColour: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)',
        };

        exteriorColors.push(otherColor);
        interiorColors.push(otherColor);

        const vehicleColorMileageFields = !isEmpty(this.state.autoCompleteValue)
            ? [
                  <div className="radio-color step-section">
                      <span
                          className={`vehicle-info-form ${
                              this.isTrade360 ? WEB_CSS_CLASSNAMES.SUBHEADING1 : 'sr-text'
                          }`}
                      >
                          {LocaleService.t('TRADE_IN.EXTERIOR_COLOR')}
                      </span>
                      <InputColorRadioContainer
                          prefix="ext-color"
                          options={uniqBy(exteriorColors, 'htmlColour')}
                          onChange={(description, colorCode) =>
                              this.handleColorRadioChange('exteriorColor', description, colorCode)
                          }
                          isValid={this.state.formValidation.exteriorColor}
                          hasBeenSubmit={this.state.hasBeenSubmitted}
                          classNames={['radio-color-container']}
                          value={this.state?.tradeInVehicle?.exteriorColor?.colorCode}
                          errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EXTERIOR_COLOR')}
                          disabled={this.context.fieldsLocked}
                      />
                  </div>,
                  <div className="radio-color step-section">
                      <span
                          className={`vehicle-info-form ${
                              this.isTrade360 ? WEB_CSS_CLASSNAMES.SUBHEADING1 : 'sr-text'
                          }`}
                      >
                          {LocaleService.t('TRADE_IN.INTERIOR_COLOR')}
                      </span>
                      <InputColorRadioContainer
                          prefix="int-color"
                          options={uniqBy(interiorColors, 'htmlColour')}
                          onChange={(description, colorCode) =>
                              this.handleColorRadioChange('interiorColor', description, colorCode)
                          }
                          isValid={this.state.formValidation.interiorColor}
                          hasBeenSubmit={this.state.hasBeenSubmitted}
                          classNames={['radio-color-container']}
                          value={this.state?.tradeInVehicle?.interiorColor?.colorCode}
                          disabled={this.context.fieldsLocked}
                      />
                  </div>,
                  <div className="clearfix step-section ">
                      {this.displayOdometer()}
                      <div className="odometer-container">
                          <InputNumber
                              id="odometer"
                              name="odometer"
                              label="KM"
                              customMessage={{
                                  required: LocaleService.t('TRADE_IN.REQUIRED'),
                                  number: LocaleService.t('TRADE_IN.ODOMETER.ERROR', {
                                      min: 0,
                                      max: 185000,
                                  }),
                              }}
                              min={0}
                              max={185000}
                              value={this.state.tradeInVehicle?.odometer || ''}
                              classNames={['styled-placeholder half']}
                              locale={LocaleService.getSimpleLocale()}
                              required
                              floatLabel
                              onChange={(value) => this.handleMileageChange(value, 'odometer')}
                              onBlur={(e) => this.handleOdometerAnalytics(e.target.value)}
                              disabled={this.context.fieldsLocked}
                              withErrorTooltip={false}
                              icon="icon"
                          />
                      </div>
                  </div>,
              ]
            : [];

        const isRenderVehicleOptions =
            !isEmpty(this.state.autoCompleteValue) &&
            !isEmpty(this.state.tradeInVehicle.options) &&
            this.props?.displayOptionsOnInfoPage;

        // For Trade360 only display vehicle options on vehicle info page
        const vehicleOptions = isRenderVehicleOptions ? (
            <div className="clearfix vehicle-options step-section">
                <VehicleOptions
                    tradeInVehicle={this.state.tradeInVehicle}
                    noTitle
                    setVehicleOptions={(selectedOptions) => this.handleVehicleOptionsChange(selectedOptions)}
                    isTrade360={this.isTrade360}
                />
            </div>
        ) : (
            ''
        );
        const vehicleFormFields = [this.getCBBAutoComplete(), ...vehicleColorMileageFields, vehicleOptions];
        return vehicleFormFields;
    }

    displayOdometer() {
        if (this.isTrade360) {
            return <p className={WEB_CSS_CLASSNAMES.HEADING4}> {LocaleService.t('TRADE_IN.ODOMETER_READING')}</p>;
        }
        return (
            <p className="sr-text trade-in-form--odometer"> {LocaleService.t('TRADE_IN.ODOMETER_READING_DILAWRI')}</p>
        );
    }

    static contextType = NewTradeInContext;

    handleColorRadioChange(name, description, colorCode) {
        const selectedColor = { description, colorCode };
        if (name === 'interiorColor') {
            AnalyticsService.ga4Trigger(TAGS.TRADE_IN_INTERIOR_COLOR_SELECTION(selectedColor?.description));
        } else if (name === 'exteriorColor') {
            AnalyticsService.ga4Trigger(TAGS.TRADE_IN_EXTERIOR_COLOR_SELECTION(selectedColor?.description));
        }
        this.setState({
            tradeInVehicle: {
                ...this.state.tradeInVehicle,
                [name]: selectedColor,
            },
            formValidation: {
                ...this.state.formValidation,
                [name]: this.isFieldValid(colorCode, description),
            },
        });
    }

    handleEdit() {
        this.setState({
            displayLabel: false,
        });
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_EDIT_VEHICLE());
    }

    handleMileageChange(value, name) {
        const { tradeInVehicle } = this.state;
        const formattedValue = value ? parseInt(value, 10) : '';
        tradeInVehicle[name] = ['year', 'odometer'].includes(name) ? formattedValue : value;

        this.setState({
            tradeInVehicle,
            formValidation: {
                ...this.state.formValidation,
                [name]: this.isFieldValid(value, name),
            },
        });
    }

    handleOdometerAnalytics(value) {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_ODOMETER(value));
    }

    handleVehicleOptionsChange(selectedVehicleOptions) {
        const tradeInVehicle = { ...this.state.tradeInVehicle, selectedVehicleOptions };
        this.setState({ tradeInVehicle });
    }

    // Only called
    fillAutoCompleteValueFromTradeInVehicle(updatedVehicle) {
        const tradeInVehicle = merge(updatedVehicle, this.state.tradeInVehicle);

        if (!isEmpty(tradeInVehicle)) {
            this.setState(
                (oldState) => {
                    return {
                        ...oldState,
                        tradeInVehicle,
                        // TODO: Find a way to use either tradeInVehicle.id or tradeInVehicle.value
                        autoCompleteValue:
                            !tradeInVehicle.id || tradeInVehicle.id === 0 ? tradeInVehicle.value : tradeInVehicle.value,
                        displayCheckInfoMessage: true,
                        displayLabel: !!tradeInVehicle?.value,
                    };
                },
                () => {
                    this.validateVehicleInformationForm(tradeInVehicle);
                    if (updatedVehicle) this.props.onTradeInVehicleUpdated(tradeInVehicle);
                }
            );
        }
    }

    validateVehicleInformationForm(vehicle) {
        const formValidation = { ...this.state.formValidation };
        forOwn(formValidation, (isValid, key) => {
            if (key !== 'identityGroup') {
                formValidation[key] = this.isFieldValid(vehicle[key], key);
            }
        });
        this.setState({ formValidation });
    }

    isFieldValid(value, name) {
        return fieldValidations[name] ? fieldValidations[name](value) : ValidationService.validateIsNotEmpty(value);
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleTradeInChange(value, name) {
        const { tradeInVehicle } = this.state;
        tradeInVehicle[name] = ['year', 'odometer'].includes(name) ? parseInt(value, 10) : value;

        this.setState(
            {
                tradeInVehicle,
                formValidation: {
                    ...this.state.formValidation,
                    [name]: this.isFieldValid(value, name),
                },
                // Set estimation to null, once trade in vehicle information changed
                // Setting estimation to null to get new estimation
                estimation: null,
            },
            this.props.onTradeInVehicleUpdated(tradeInVehicle)
        );
    }

    sendVehicleBasicInfo() {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_NEXT_BTN());
        // If fields are locked, no need to update token, directly go to next step
        if (this.context.fieldsLocked) return this.props.onNextStep();

        return Promise.resolve(ValidationService.validateForm(this.state.formValidation)).then((isFormValid) => {
            if (!isFormValid) {
                this.setState({
                    hasBeenSubmitted: true,
                });
                return false;
            }

            this.setState({ displaySubmitError: false, fetching: true });
            const { tradeInVehicle } = this.state;
            const selectedOptions = tradeInVehicle?.selectedVehicleOptions
                ? tradeInVehicle.selectedVehicleOptions.map((option) => option.code)
                : null;
            // If fields are locked, no need to update token
            if (this.context.fieldsLocked) return this.props.onNextStep();

            // Get the trade in vehicle token and valuation token
            // Pass this valuation token along with trade vehicle info to the next screen
            // TODO: Need validation to call this API if comes from previous page and page info not changed??
            // TODO: No need to call this API if screen is locked after step5
            ShowroomCBBService.updateTradeVehicleToken(
                tradeInVehicle.tradeVehicleToken,
                tradeInVehicle?.odometer,
                tradeInVehicle?.exteriorColor?.description,
                tradeInVehicle?.interiorColor?.description,
                selectedOptions,
                tradeInVehicle?.vin,
                null,
                false,
                null,
                this.props.config
            )
                .then((result) => {
                    tradeInVehicle.tradeVehicleToken = result.tradeVehicleToken;
                    this.props.onTradeInVehicleUpdated(tradeInVehicle);
                    return this.props.onFormSubmit(tradeInVehicle);
                })
                .catch((err) => {
                    this.setState({
                        displayAPISubmitError: err,
                        fetching: false,
                    });
                });
            // DON'T pass to second screen if CBB API returns an error
            return false;
        });
    }

    render() {
        return (
            <div className={`vehicle-info-form ${this.isTrade360 ? 'trade-360' : ''}`}>
                {this.state.displayAPISubmitError && (
                    // Display an error box if CBB API returns an error on valuation token generation
                    <ErrorMessage message={this.state.displayAPISubmitError} />
                )}
                <div className="vehicle-info-form-body">
                    <form className="trade-in-form" onSubmit={this.handleSubmit}>
                        <div className="trade-in-cta">
                            {this.state.displaySubmitError && (
                                <MessageBox
                                    level="error"
                                    message={
                                        this.state.displayNotFoundError
                                            ? LocaleService.t('TRADE_IN.VEHICLE_NOT_FOUND')
                                            : LocaleService.t('TRADE_IN.ERROR')
                                    }
                                />
                            )}
                        </div>
                        <section className="clearfix vehicle-info-form-body--vehicle-details">
                            {this.getVehicleBasicInfoForm()}
                        </section>

                        {this.state.autoCompleteValue && (
                            <section
                                className={`clearfix ${this.isTrade360 ? '' : 'vehicle-buttons-container'}`}
                                data-test="trade-in.vehicle-form.submit"
                            >
                                <div className="vehicle-buttons">
                                    {this.isTrade360 && (
                                        <ActionButton noPrefix noArrow disabled>
                                            {LocaleService.t('TRADE_IN.BUTTONS.BACK')}
                                        </ActionButton>
                                    )}
                                    <ActionButton
                                        next
                                        onClick={this.sendVehicleBasicInfo}
                                        loading={this.state.fetching}
                                        noArrow={this.isTrade360}
                                        noPrefix={this.isTrade360}
                                    >
                                        {this.props.nextLabel || LocaleService.t('TRADE_IN.BUTTONS.NEXT')}
                                    </ActionButton>
                                </div>
                            </section>
                        )}
                    </form>
                </div>
            </div>
        );
    }
}

VehicleInfo.defaultProps = {
    updateAccount: noop,
    useCBB: PropTypes.bool.isRequired,
    quote: null,
    account: new Account(),
    nextLabel: null,
    config: {},
};

VehicleInfo.propTypes = {
    onFormSubmit: PropTypes.func.isRequired,
    onTradeInVehicleUpdated: PropTypes.func.isRequired,
    isOfficialTradeIn: PropTypes.bool.isRequired,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    account: PropTypes.instanceOf(Account),
    nextLabel: PropTypes.string,
    config: PropTypes.object,
};

VehicleInfo.displayName = 'VehicleInfo';

export default VehicleInfo;
